import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import SpinnerButton from "@/components/SpinnerButton";
import WonDealFields from "@/components/WonDealFields";
import { publicApi } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const STATUSES = ["New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];

// Mirrors LOST_REASONS in backend/server.py, which validates the value
// on save. Hardcoded here the same way STATUSES already is — keep the
// two lists in step.
const LOST_REASONS = ["Price", "Bought elsewhere", "Stock unavailable", "No response from customer", "Not a serious buyer", "Other"];

export default function UpdateLead() {
  usePageMeta({ title: "Update Lead \u2014 SCW Contact Hub", robots: "noindex, nofollow" });
  const { lead_id } = useParams();
  // M1 — lead-scoped signed token from the emailed link. Required by
  // both endpoints; without it the API returns 403 and the error below
  // tells the user to go through the dashboard instead.
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [note, setNote] = useState("");
  const [wonValue, setWonValue] = useState("");
  const [wonSku, setWonSku] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    publicApi.leadPublic(lead_id, token)
      .then((r) => { setLead(r.data); setStatus(r.data.status); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load lead."));
  }, [lead_id, token]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true); setSaved(false); setError("");
    try {
      const body = { status, note };
      if (status === "Closed Lost") { body.lost_reason = lostReason; }
      if (status === "Closed Won") {
        body.won_value = wonValue;
        body.won_sku = wonSku;
      }
      const res = await publicApi.leadUpdatePublic(lead_id, body, token);
      setLead({
        ...lead,
        status: res.data.lead.Status,
        notes: res.data.lead.Notes,
        status_history: res.data.lead.Status_History,
      });
      setNote("");
      setSaved(true);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Could not update.");
    } finally { setLoading(false); }
  };

  if (!lead) {
    return <PublicLayout><div style={{ maxWidth: 620, margin: "0 auto", padding: "56px 28px" }}>{error || "Loading\u2026"}</div></PublicLayout>;
  }

  return (
    <PublicLayout>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 28px 80px 28px" }} data-testid="update-lead-page">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Internal &middot; update lead</p>
        <h1 style={{ fontSize: 28, margin: "0 0 24px 0" }}>{lead.customer_name}</h1>

        <div style={{ borderTop: "1px solid var(--scw-line)", marginBottom: 28 }}>
          {[
            ["Type", lead.type], ["Phone", lead.customer_phone], ["Email", lead.customer_email],
            ["Product", lead.product || "\u2014"], ["Message", lead.message || "\u2014"],
            ["Appointment", lead.appointment_date ? `${lead.appointment_date} ${lead.appointment_time}` : "\u2014"],
            ["Received", lead.timestamp + " MYT"],
            ["Duty person", lead.duty_person || "\u2014"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid var(--scw-line)" }}>
              <div style={{ width: 150, color: "var(--scw-muted)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>{k}</div>
              <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>{v}</div>
            </div>
          ))}
        </div>

        <form onSubmit={save} data-testid="update-form">
          <div style={{ marginBottom: 22 }}>
            <label className="scw-label">Status</label>
            <select
              className="scw-select"
              value={status}
              onChange={(e) => {
                const next = e.target.value;
                setStatus(next);
                if (next === "Closed Won" && !wonSku && lead.product) {
                  setWonSku(lead.product);
                }
              }}
              data-testid="update-status-select"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <WonDealFields
            visible={status === "Closed Won"}
            wonValue={wonValue}
            wonSku={wonSku}
            onChangeValue={setWonValue}
            onChangeSku={setWonSku}
          />
          {/* Pass 56 — required on the flip to Closed Lost. The server
              validates it too; this is the affordance, not the gate. */}
          {status === "Closed Lost" && (
            <div style={{ marginBottom: 18 }}>
              <label className="scw-label">Reason lost</label>
              <select
                className="scw-select"
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                data-testid="update-lost-reason"
              >
                <option value="">Select a reason…</option>
                {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 22 }}>
            <label className="scw-label">Append a note</label>
            <textarea className="scw-textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called customer at 4pm. Booked viewing for Saturday." data-testid="update-note-input" />
          </div>
          {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="update-error">{error}</p>}
          <SpinnerButton loading={loading} dataTestId="update-save-btn">Save</SpinnerButton>
          {saved && (
            <div
              style={{
                marginTop: 18,
                padding: "14px 16px",
                background: "#f4faf4",
                border: "1px solid #cce4cc",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
              data-testid="update-success-block"
            >
              <p
                style={{ color: "#3a7d3a", fontSize: 13, margin: 0 }}
                data-testid="update-success"
              >
                Status updated.
              </p>
              <Link
                to="/team"
                className="scw-btn scw-btn-ghost"
                style={{ alignSelf: "flex-start", padding: "8px 16px", textDecoration: "none" }}
                data-testid="update-go-dashboard-link"
              >
                Go to my dashboard {'\u2192'}
              </Link>
            </div>
          )}
        </form>

        <div style={{ marginTop: 40 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Notes (chronological)</p>
          <pre style={{ background: "#fafafa", padding: 16, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }} data-testid="update-notes-view">{lead.notes || "\u2014"}</pre>
        </div>
        <div style={{ marginTop: 24 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Status history</p>
          <pre style={{ background: "#fafafa", padding: 16, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }} data-testid="update-history-view">{lead.status_history || "\u2014"}</pre>
        </div>
      </div>
    </PublicLayout>
  );
}
